import React, { useEffect, useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { supabase } from './lib/supabase';
import AdminDashboard from './components/admin/AdminDashboard';
import StakeholderShell from './components/stakeholder/StakeholderShell';
import LoginPage from './components/LoginPage';
import AuthCallback from './components/auth/AuthCallback';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Loader2, Lock, CheckCircle } from 'lucide-react';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import { toast } from 'sonner';

const API = '/api';
const hasAuthCallbackParams = (location) => {
  const searchParams = new URLSearchParams(location.search || '');
  const hashParams = new URLSearchParams((location.hash || '').replace(/^#/, ''));

  return (
    searchParams.has('code') ||
    searchParams.has('error') ||
    hashParams.has('access_token') ||
    hashParams.has('refresh_token') ||
    hashParams.has('error') ||
    hashParams.has('session_id')
  );
};

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        return;
      }

      try {
        const response = await fetch(`${API}/auth/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        setStatus(response.ok ? 'success' : 'error');
      } catch (error) {
        setStatus('error');
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-screen bg-charcoal-dark flex items-center justify-center p-4">
      <div className="bg-charcoal-light rounded-2xl border border-charcoal-lighter p-8 max-w-md w-full text-center">
        {status === 'verifying' && (
          <>
            <Loader2 className="w-12 h-12 text-aqua animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white">Verifying your email...</h2>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Email Verified!</h2>
            <p className="text-gray-400 mb-6">Your email has been verified. You can now access the command center.</p>
            <Button onClick={() => window.location.assign('/')} className="bg-aqua hover:bg-aqua-dark text-charcoal">
              Continue
            </Button>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <span className="text-red-500 text-2xl">✕</span>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Verification Failed</h2>
            <p className="text-gray-400 mb-6">The verification link is invalid or has expired.</p>
            <Button onClick={() => window.location.assign('/login')} variant="outline" className="border-charcoal-lighter text-white">
              Back to Login
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

const getRecoveryParamsFromWindow = () => {
  if (typeof window === 'undefined') {
    return {
      code: null,
      tokenHash: null,
      type: null,
      hasHashTokens: false,
      hasRecoveryLink: false,
    };
  }

  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams(url.search || '');
  const hashParams = new URLSearchParams((url.hash || '').replace(/^#/, ''));
  const type = searchParams.get('type') || hashParams.get('type');
  const code = searchParams.get('code') || hashParams.get('code');
  const tokenHash = searchParams.get('token_hash') || hashParams.get('token_hash');
  const hasHashTokens = hashParams.has('access_token') || hashParams.has('refresh_token');

  return {
    code,
    tokenHash,
    type,
    hasHashTokens,
    hasRecoveryLink: Boolean(code || tokenHash || hasHashTokens || type === 'recovery'),
  };
};

const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('checking');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let didReceiveRecoveryEvent = false;
    const recoveryParams = getRecoveryParamsFromWindow();

    const markReady = () => {
      if (cancelled) return;
      setReady(true);
      setStatus('form');
      window.history.replaceState({}, '', '/reset-password');
    };

    const getCurrentSession = async () => {
      const { data } = await supabase.auth.getSession();
      return data?.session ?? null;
    };

    const waitForSession = async (timeoutMs = 5000) => {
      const deadline = Date.now() + timeoutMs;

      while (Date.now() < deadline) {
        const session = await getCurrentSession();
        if (session?.access_token) return session;
        await new Promise((resolve) => setTimeout(resolve, 250));
      }

      return null;
    };

    (async () => {
      try {
        if (!recoveryParams.hasRecoveryLink) {
          throw new Error('Please open the latest password reset link from your email.');
        }

        if (recoveryParams.code || recoveryParams.tokenHash) {
          try {
            await supabase.auth.signOut({ scope: 'local' });
          } catch {
            // Ignore sign-out issues; this just clears stale browser auth before recovery.
          }
        }

        let session = recoveryParams.hasHashTokens ? await waitForSession(4000) : null;

        if (!session && recoveryParams.tokenHash && recoveryParams.type === 'recovery') {
          const { error: verifyError } = await supabase.auth.verifyOtp({
            type: 'recovery',
            token_hash: recoveryParams.tokenHash,
          });
          if (verifyError) throw verifyError;
          session = await waitForSession(5000);
        }

        if (!session && recoveryParams.code) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(recoveryParams.code);
          if (exchangeError) throw exchangeError;
          session = await waitForSession(5000);
        }

        if (!session && didReceiveRecoveryEvent) {
          session = await waitForSession(5000);
        }

        if (!session?.access_token) {
          throw new Error('No recovery session was established from this reset link.');
        }

        markReady();
      } catch (error) {
        if (!cancelled) {
          setReady(false);
          setStatus('error');
        }
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        didReceiveRecoveryEvent = true;
      }

      if ((event === 'PASSWORD_RECOVERY' || recoveryParams.hasHashTokens) && session?.access_token) {
        markReady();
      }
    });

    return () => {
      cancelled = true;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!ready) {
      toast.error('Please open the password reset link from your email first.');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        toast.error(error.message || 'Reset failed');
        setStatus('error');
      } else {
        setStatus('success');
      }
    } catch (error) {
      toast.error('An error occurred');
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-charcoal-dark flex items-center justify-center p-4">
      <div className="bg-charcoal-light rounded-2xl border border-charcoal-lighter p-8 max-w-md w-full">
        {status === 'checking' && (
          <div className="text-center text-gray-400">
            Checking your reset link...
          </div>
        )}

        {status === 'form' && (
          <>
            <h2 className="text-2xl font-bold text-white mb-2 text-center">Reset Password</h2>
            <p className="text-gray-400 text-center mb-6">Enter your new password</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-charcoal border-charcoal-lighter text-white"
                    placeholder="At least 8 characters"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1 block">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 bg-charcoal border-charcoal-lighter text-white"
                    placeholder="Confirm password"
                    required
                  />
                </div>
              </div>

              <Button type="submit" disabled={loading} className="w-full bg-aqua hover:bg-aqua-dark text-charcoal font-semibold py-6">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Reset Password'}
              </Button>
            </form>
          </>
        )}

        {status === 'success' && (
          <div className="text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Password Reset!</h2>
            <p className="text-gray-400 mb-6">Your password has been reset successfully.</p>
            <Button onClick={() => window.location.assign('/login')} className="bg-aqua hover:bg-aqua-dark text-charcoal">
              Sign In
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <span className="text-red-500 text-2xl">✕</span>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Reset Failed</h2>
            <p className="text-gray-400 mb-6">This reset link may have expired. Please request a new one.</p>
            <Button onClick={() => window.location.assign('/login')} variant="outline" className="border-charcoal-lighter text-white">
              Back to Login
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const StakeholderCompatibilityRedirect = () => {
  const location = useLocation();
  const communityPathname = location.pathname.replace(/^\/stakeholder(?=\/|$)/, '/community');
  const destination = `${communityPathname}${location.search || ''}${location.hash || ''}`;

  return <Navigate replace to={destination} />;
};

const AppRouter = () => {
  const location = useLocation();

  if (location.pathname === '/auth/callback' || (location.pathname !== '/reset-password' && hasAuthCallbackParams(location))) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route
        path="/community/*"
        element={
          <ProtectedRoute requireAdmin>
            <StakeholderShell />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stakeholder/*"
        element={
          <ProtectedRoute requireAdmin>
            <StakeholderCompatibilityRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute requireAdmin>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/*"
        element={
          <ProtectedRoute requireAdmin>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-right" />
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
